import { Injectable } from '@angular/core';
import { getIDType } from '@datorama/akita';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DatasourceService, DatasourceTypeQuery } from '@y42/datasource';
import {
  METADATA_JSON_SCHEMA_URL,
  SQL_MODELING_MODEL_JSON_SCHEMA_URL,
  SQL_MODELING_MODEL_TABLE_JSON_SCHEMA_URL,
  Y42MetadataGitModel,
} from '@y42/feature/json-schema';
import { Y42RouteDetailTabs, Y42RouterService, Y42Routes } from '@y42/feature/routing';
import { AddEntityDialogResponse } from '@y42/feature/shared/add-entity';
import { GitQuery } from '@y42/git';
import { TriggerService } from '@y42/impex';
import { DatasourceTypeEnum, Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { getSafeString } from '@y42/util/safe-string';
import uniq from 'lodash/uniq';
import {
  SqlModelingGitService,
  SqlModelingQuery,
  SqlModelingState,
  SqlModelingStore,
  SqlModelingTableGitFactoryService,
  SqlModelingTableJobStore,
  SqlModelingTableQuery,
  SqlModelingTableStore,
  SqlTable,
} from '../../state';
import { SqlModelingTableJobService } from '../datasource-table-job/sql-modeling-table-job.service';
import { SqlModelingTableService } from '../datasource-table/sql-modeling-table.service';
import { SqlModelingAclService } from './sql-modeling-acl.service';
import { SqlModelingUserDataService } from './sql-modeling-user-data.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class SqlModelingService extends DatasourceService<SqlModelingState> {
  constructor(
    protected readonly userData: SqlModelingUserDataService,
    protected readonly store: SqlModelingStore,
    protected readonly query: SqlModelingQuery,
    protected readonly git: SqlModelingGitService,
    protected readonly tableStore: SqlModelingTableStore,
    protected readonly tableGitFactory: SqlModelingTableGitFactoryService,
    protected readonly tableJobStore: SqlModelingTableJobStore,
    protected readonly routerService: Y42RouterService,
    protected readonly toast: ToastService,
    protected readonly gitQuery: GitQuery,
    protected readonly acl: SqlModelingAclService,
    protected readonly tableQuery: SqlModelingTableQuery,
    protected readonly tableService: SqlModelingTableService,
    private readonly datasourceTypeQuery: DatasourceTypeQuery,
    triggerService: TriggerService,
    tableJobService: SqlModelingTableJobService,
  ) {
    super(
      {
        featureType: Y42FeatureType.SqlModel,
        userData,
        store,
        query,
        acl,
        git,
        toast,
        tableStore,
        tableQuery,
        tableJobStore,
        tableJobService,
        gitQuery,
      },
      triggerService,
    );
  }

  async create(data: AddEntityDialogResponse) {
    const { title, description } = data;
    const $name = getSafeString(title);
    const typeModel = this.datasourceTypeQuery.findModelByType(DatasourceTypeEnum.SqlModel)!;

    const model = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name,
      name: $name,
      description,
      settings: {
        $schema: SQL_MODELING_MODEL_JSON_SCHEMA_URL,
      },
    });
    await this.store.pending;

    await this.git.commitChanges(`models: new ${typeModel.displayName} model "${$name}" created`, model.$path);

    await this.routerService.navigateToDetail(Y42Routes.SqlModeling, $name, Y42RouteDetailTabs.Overview);
  }

  public async upsertSettingsSql(config: { models: SqlTable[]; modelFilePath: string }) {
    const relatedModel = this.query.getUIEntity(config.modelFilePath);
    if (!relatedModel) {
      return;
    }

    const tablesOrder = config.models.map((model) => model.table_name);
    await this.update(
      {
        ...relatedModel,
        settings: {
          ...relatedModel.settings,
          tablesOrder,
        },
      },
      relatedModel.$path,
    );

    const tables = this.tableQuery.getAll().filter((t) => t.$parent === relatedModel.$path);
    const existingTables = tables ? Object.values(tables) : [];

    const allPossibleTableNames = uniq([
      ...existingTables.map((entity) => entity.$name),
      ...config.models.map((model) => model.table_name),
    ]);

    for (const tableName of allPossibleTableNames) {
      const tableInModels = config.models.find((model) => model.table_name === tableName);
      const tableInExistingTables = existingTables.find((existingTable) => existingTable.$name === tableName);

      if (tableInModels && tableInExistingTables) {
        // update
        await relatedModel.setSqlQuery?.(tableName, tableInModels.sql);
      } else if (!tableInModels && tableInExistingTables) {
        // delete
        await this.tableService.delete(tableInExistingTables.$path);
      } else if (tableInModels && !tableInExistingTables) {
        // create
        await this.tableService.createFor(relatedModel, tableInModels.table_name, {
          $schema: SQL_MODELING_MODEL_TABLE_JSON_SCHEMA_URL,
        });
        await relatedModel.setSqlQuery?.(tableInModels.table_name, tableInModels.sql);
      }
    }
  }

  async delete($path: string = this.query.getActiveId() as getIDType<SqlModelingState>) {
    const entity = this.query.getEntity($path);
    this.store.delete($path);
    await this.store.pending;
    await this.git.commitChanges(`models: model '${entity?.$name ?? $path}' deleted`, [$path]);
  }
}
